<template>
    <div class="grid grid-cols-6 gap-6">
        <div class="col-span-6 sm:col-span-4">
            <label for="name" class="block text-sm font-medium text-gray-700">
                Full Name
            </label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    v-model="inputs.name"
                    :ref="refs.name"
                    id="name"
                    name="name"
                    type="text"
                    autocomplete="name"
                    required
                    :class="[
                        hasError(errors, 'name')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="hasError(errors, 'name') ? 'true' : 'false'"
                    :aria-describedby="
                        hasError(errors, 'name') ? 'name-error' : ''
                    "
                />
                <div
                    v-if="hasError(errors, 'name')"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="hasError(errors, 'name')"
                class="mt-2 text-sm text-red-600"
                id="name-error"
            >
                {{ error(errors, "name") }}
            </p>
        </div>

        <div
            v-if="includeDefaultField"
            :class="[
                includeVerifyField ? 'sm:col-span-1' : 'sm:col-span-2',
                'col-span-6 order-last sm:order-none',
            ]"
        >
            <label
                class="block text-sm sm:text-center font-medium text-gray-700"
            >
                Set as Default Address
            </label>
            <input
                type="hidden"
                name="default"
                :value="inputs.default ? '1' : ''"
            />
            <SwitchGroup
                as="li"
                class="pt-3 pb-1 flex items-center justify-between sm:justify-center text-transparent"
            >
                <Switch
                    data-test="default"
                    name="default"
                    v-model="inputs.default"
                    :class="[
                        inputs.default ? 'bg-nutreance-green' : 'bg-gray-200',
                        'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
                    ]"
                >
                    <span class="sr-only">Toggle Default</span>
                    <span
                        aria-hidden="true"
                        :class="[
                            inputs.default ? 'translate-x-5' : 'translate-x-0',
                            'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    />
                </Switch>
            </SwitchGroup>
        </div>

        <div
            v-if="includeVerifyField"
            :class="[
                includeDefaultField ? 'sm:col-span-1' : 'sm:col-span-2',
                'col-span-6 order-last sm:order-none',
            ]"
        >
            <label
                class="block text-sm sm:text-center font-medium text-gray-700"
            >
                Verify Address
            </label>
            <input
                type="hidden"
                name="verify"
                :value="inputs.verify ? '1' : ''"
            />
            <SwitchGroup
                as="li"
                class="pt-3 pb-1 flex items-center justify-between sm:justify-center text-transparent"
            >
                <Switch
                    data-test="verify"
                    name="verify"
                    v-model="inputs.verify"
                    :class="[
                        inputs.verify ? 'bg-nutreance-green' : 'bg-gray-200',
                        'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
                    ]"
                >
                    <span class="sr-only">Toggle Verify</span>
                    <span
                        aria-hidden="true"
                        :class="[
                            inputs.verify ? 'translate-x-5' : 'translate-x-0',
                            'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    />
                </Switch>
            </SwitchGroup>
        </div>

        <div class="col-span-6">
            <label for="line1" class="block text-sm font-medium text-gray-700">
                {{ ucwords(country.line1) }}
            </label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    v-model="inputs.line1"
                    :ref="refs.line1"
                    @placeChanged="onPlaceChanged"
                    @keydown.enter="onEnterKeyDown"
                    @change="onAddressChanged"
                    name="line1"
                    id="line1"
                    type="text"
                    required
                    placeholder=""
                    autocomplete="address-line1"
                    :class="[
                        hasError(errors, 'line1')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        verificationErrors.line1 ? 'pr-10' : '',
                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="hasError(errors, 'line1') ? 'true' : 'false'"
                    :aria-describedby="
                        hasError(errors, 'line1') ? 'line1-error' : ''
                    "
                />
                <div
                    v-if="
                        hasError(errors, 'line1') && !verificationErrors.line1
                    "
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
                <div
                    v-if="verificationErrors.line1"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <QuestionMarkCircleIcon
                        class="h-5 w-5 text-amber-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="hasError(errors, 'line1')"
                class="mt-2 text-sm text-red-600"
                id="line1-error"
            >
                {{ error(errors, "line1") }}
            </p>
            <p
                v-if="verificationErrors.line1"
                class="mt-2 text-sm text-amber-600"
                id="line1-verification-error"
            >
                {{ verificationErrors.line1 }}
            </p>
        </div>

        <div class="col-span-6">
            <label for="line2" class="block text-sm font-medium text-gray-700">
                {{ ucwords(country.line2) }}
            </label>

            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    v-model="inputs.line2"
                    :ref="refs.line2"
                    @change="onAddressChanged"
                    name="line2"
                    id="line2"
                    type="text"
                    autocomplete="address-line2"
                    placeholder="(optional)"
                    :class="[
                        hasError(errors, 'line2')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        verificationErrors.line2 ? 'pr-10' : '',
                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="hasError(errors, 'line2') ? 'true' : 'false'"
                    :aria-describedby="
                        hasError(errors, 'line2') ? 'line2-error' : ''
                    "
                />
                <div
                    v-if="
                        hasError(errors, 'line2') && !verificationErrors.line2
                    "
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
                <div
                    v-if="verificationErrors.line2"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <QuestionMarkCircleIcon
                        class="h-5 w-5 text-amber-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="hasError(errors, 'line2')"
                class="mt-2 text-sm text-red-600"
                id="line2-error"
            >
                {{ error(errors, "line2") }}
            </p>
            <p
                v-if="verificationErrors.line2"
                class="mt-2 text-sm text-amber-600"
                id="line2-verification-errror"
            >
                {{ verificationErrors.line2 }}
            </p>
        </div>

        <div
            v-if="inputs.city || country.city"
            class="col-span-6 sm:col-span-6 lg:col-span-3"
        >
            <label for="city" class="block text-sm font-medium text-gray-700">
                {{ country.city ? ucwords(country.city) : "City" }}
            </label>

            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    v-model="inputs.city"
                    :ref="refs.city"
                    @change="onAddressChanged"
                    name="city"
                    id="city"
                    type="text"
                    autocomplete="address-level2"
                    required
                    :class="[
                        hasError(errors, 'city')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="hasError(errors, 'city') ? 'true' : 'false'"
                    :aria-describedby="
                        hasError(errors, 'city') ? 'city-error' : ''
                    "
                />
                <div
                    v-if="hasError(errors, 'city')"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="hasError(errors, 'city')"
                class="mt-2 text-sm text-red-600"
                id="city-error"
            >
                {{ error(errors, "city") }}
            </p>
        </div>

        <div
            v-if="inputs.state || country.state"
            class="col-span-6 sm:col-span-3"
        >
            <label for="state" class="block text-sm font-medium text-gray-700">
                {{
                    country.state
                        ? ucwords(country.state)
                        : ucwords(country.state_default)
                }}
            </label>

            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    v-if="isEmpty(states)"
                    v-model="inputs.state"
                    :ref="refs.state"
                    @change="onAddressChanged"
                    name="state"
                    id="state"
                    type="text"
                    autocomplete="address-level1"
                    required
                    :class="[
                        hasError(errors, 'state')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="hasError(errors, 'state') ? 'true' : 'false'"
                    :aria-describedby="
                        hasError(errors, 'state') ? 'state-error' : ''
                    "
                />
                <select
                    v-else
                    v-model="inputs.state"
                    :ref="refs.state"
                    @change="onAddressChanged"
                    id="state"
                    name="state"
                    autocomplete="address-level1"
                    required
                    :class="[
                        hasError(errors, 'state')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="hasError(errors, 'state') ? 'true' : 'false'"
                    :aria-describedby="
                        hasError(errors, 'state') ? 'state-error' : ''
                    "
                >
                    <option value="" disabled hidden selected>
                        --- Select a {{ ucwords(country.state) }} ---
                    </option>
                    <option
                        v-for="(state, code) in states"
                        :key="code"
                        :value="code"
                    >
                        {{ state }}
                    </option>
                </select>
                <div
                    v-if="hasError(errors, 'state')"
                    :class="[
                        isEmpty(states) ? 'pr-3' : 'pr-8',
                        'absolute inset-y-0 right-0 flex items-center pointer-events-none',
                    ]"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="hasError(errors, 'state')"
                class="mt-2 text-sm text-red-600"
                id="state-error"
            >
                {{ error(errors, "state") }}
            </p>
        </div>

        <div
            v-if="inputs.postal_code || country.postal_code"
            class="col-span-6 sm:col-span-3 lg:col-span-2"
        >
            <label
                for="postal_code"
                class="block text-sm font-medium text-gray-700"
            >
                {{
                    country.postal_code
                        ? ucwords(country.postal_code)
                        : "Postal Code"
                }}
            </label>

            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    v-model="inputs.postal_code"
                    :ref="refs.postal_code"
                    @change="onAddressChanged"
                    name="postal_code"
                    id="postal_code"
                    type="text"
                    autocomplete="postal-code"
                    :required="country.shipping_region_field === 'postal_code'"
                    :class="[
                        hasError(errors, 'postal_code')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        verificationErrors.postal_code ? 'pr-10' : '',
                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="
                        hasError(errors, 'postal_code') ? 'true' : 'false'
                    "
                    :aria-describedby="
                        hasError(errors, 'postal_code')
                            ? 'postal_code-error'
                            : ''
                    "
                />
                <div
                    v-if="
                        hasError(errors, 'postal_code') &&
                        !verificationErrors.postal_code
                    "
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
                <div
                    v-if="verificationErrors.postal_code"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <QuestionMarkCircleIcon
                        class="h-5 w-5 text-amber-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="hasError(errors, 'postal_code')"
                class="mt-2 text-sm text-red-600"
                id="postal_code-error"
            >
                {{ error(errors, "postal_code") }}
            </p>
            <p
                v-if="verificationErrors.postal_code"
                class="mt-2 text-sm text-amber-600"
                id="postal_code-verification-errror"
            >
                {{ verificationErrors.postal_code }}
            </p>
        </div>

        <div class="col-span-6 sm:col-span-4">
            <label
                for="country_code"
                class="block text-sm font-medium text-gray-700"
            >
                Country
            </label>

            <div class="mt-1 relative rounded-md shadow-sm">
                <select
                    v-model="inputs.country_code"
                    :ref="refs.country_code"
                    @change="updateCountry"
                    id="country_code"
                    name="country_code"
                    autocomplete="country"
                    required
                    :class="[
                        hasError(errors, 'country_code')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="
                        hasError(errors, 'country_code') ? 'true' : 'false'
                    "
                    :aria-describedby="
                        hasError(errors, 'country_code')
                            ? 'country_code-error'
                            : ''
                    "
                >
                    <option
                        v-for="(country, code) in countries"
                        :key="code"
                        :value="code"
                    >
                        {{ country }}
                    </option>
                </select>
                <div
                    v-if="hasError(errors, 'country_code')"
                    class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="hasError(errors, 'country_code')"
                class="mt-2 text-sm text-red-600"
                id="country_code-error"
            >
                {{ error(errors, "country_code") }}
            </p>
        </div>

        <div class="col-span-6 sm:col-span-3">
            <label for="email" class="block text-sm font-medium text-gray-700">
                Email
            </label>

            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    v-model="inputs.email"
                    :ref="refs.email"
                    @focus="onAddressChanged"
                    name="email"
                    id="email"
                    type="email"
                    autocomplete="email"
                    placeholder="(optional)"
                    :class="[
                        hasError(errors, 'email')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="hasError(errors, 'email') ? 'true' : 'false'"
                    :aria-describedby="
                        hasError(errors, 'email') ? 'email-error' : ''
                    "
                />
                <div
                    v-if="hasError(errors, 'email')"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
                <InputTooltip
                    v-else
                    message="Recommended if you want to track your order or save your payment method"
                />
            </div>
            <p
                v-if="hasError(errors, 'email')"
                class="mt-2 text-sm text-red-600"
                id="email-error"
            >
                {{ error(errors, "email") }}
            </p>
        </div>

        <div class="col-span-6 sm:col-span-3">
            <label for="phone" class="block text-sm font-medium text-gray-700">
                Phone
            </label>

            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    v-model="inputs.phone"
                    :ref="refs.phone"
                    @focus="onAddressChanged"
                    name="phone"
                    id="phone"
                    type="tel"
                    autocomplete="phone"
                    placeholder="(optional)"
                    :class="[
                        hasError(errors, 'phone')
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="hasError(errors, 'phone') ? 'true' : 'false'"
                    :aria-describedby="
                        hasError(errors, 'phone') ? 'phone-error' : ''
                    "
                />
                <div
                    v-if="hasError(errors, 'phone')"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
                <InputTooltip
                    v-else
                    message="In case we need to contact you about your order"
                />
            </div>
            <p
                v-if="hasError(errors, 'phone')"
                class="mt-2 text-sm text-red-600"
                id="phone-error"
            >
                {{ error(errors, "phone") }}
            </p>
        </div>

        <div v-show="inputs.email" class="col-span-6">
            <div class="relative flex items-start">
                <div class="flex items-center h-5">
                    <input
                        v-model="inputs.email_opt_in"
                        id="email_opt_in"
                        name="email_opt_in"
                        type="checkbox"
                        class="focus:ring-transparent h-4 w-4 text-nutreance-green border-gray-300 rounded"
                    />
                </div>
                <div class="ml-3 text-sm">
                    <label for="email_opt_in" class="text-gray-900"
                        >I would like to receive emails about coupons,
                        discounts, and related products.</label
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
import {
    ExclamationCircleIcon,
    QuestionMarkCircleIcon,
} from "@heroicons/vue/solid";
import { Switch, SwitchGroup } from "@headlessui/vue";
import { useForm } from "../../functions/useForm";
import { useGoogleMapsAutocomplete } from "../../functions/useGoogleMapsAutocomplete";
import { useString } from "../../functions/useString";
import { useVerifyAddress } from "../../functions/useVerifyAddress";
import { isEmpty } from "lodash";
import InputTooltip from "../InputTooltip.vue";
import { updateCountry as updateCountryRequest } from "../../api/countries";

const props = withDefaults(
    defineProps<{
        address?: any;
        csrf?: string;
        countries?: any;
        default?: string;
        emailOptIn?: string;
        errors?: any;
        includeDefaultField?: boolean;
        includeVerifyField?: boolean;
        initialCountry?: any;
        initialStates?: any;
        old?: any;
        type?: string;
    }>(),
    {
        includeDefaultField: true,
        includeVerifyField: false,
        type: "create",
    },
);

const { error, hasError, oldInput } = useForm();
const {
    destroyAutocomplete,
    initializeAutocomplete,
    onEnterKeyDown,
    pasteAutocompletedAddress,
    restrictAutocompleteByCountry,
} = useGoogleMapsAutocomplete();
const { ucwords } = useString();
const { verificationErrors, verifyAddress } = useVerifyAddress();

const country = ref(props.initialCountry);
const states = ref(props.initialStates);
const oldCountryCode =
    props.type === "edit" || props.type === "copy"
        ? oldInput(props.old, "country_code")
            ? oldInput(props.old, "country_code")
            : props.address.country_code
        : oldInput(props.old, "country_code");

if (oldCountryCode && oldCountryCode !== country.value.code) {
    updateCountry(oldCountryCode);
}

const refs: Record<string, any> = {
    name: ref<any>(null),
    line1: ref<any>(null),
    line2: ref<any>(null),
    city: ref<any>(null),
    state: ref<any>(null),
    postal_code: ref<any>(null),
    country_code: ref<any>(null),
    email: ref<any>(null),
    phone: ref<any>(null),
};

const inputs =
    props.type === "edit" || props.type === "copy"
        ? reactive({
              name: oldInput(props.old, "name")
                  ? oldInput(props.old, "name")
                  : props.address.name,
              line1: oldInput(props.old, "line1")
                  ? oldInput(props.old, "line1")
                  : props.address.line1,
              line2: oldInput(props.old, "line2")
                  ? oldInput(props.old, "line2")
                  : props.address.line2,
              city: oldInput(props.old, "city")
                  ? oldInput(props.old, "city")
                  : props.address.city,
              state: oldInput(props.old, "state")
                  ? oldInput(props.old, "state")
                  : props.address.state,
              postal_code: oldInput(props.old, "postal_code")
                  ? oldInput(props.old, "postal_code")
                  : props.address.postal_code,
              country_code: country.value.code,
              email: oldInput(props.old, "email")
                  ? oldInput(props.old, "email")
                  : props.address.email,
              phone: oldInput(props.old, "phone")
                  ? oldInput(props.old, "phone")
                  : props.address.phone,
              default: !!(Object.prototype.hasOwnProperty.call(
                  props.old,
                  "default",
              )
                  ? oldInput(props.old, "default")
                  : props.default),
              verify: !!(Object.prototype.hasOwnProperty.call(
                  props.old,
                  "verify",
              )
                  ? oldInput(props.old, "verify")
                  : true),
              email_opt_in: !!(Object.prototype.hasOwnProperty.call(
                  props.old,
                  "email_opt_in",
              )
                  ? oldInput(props.old, "email_opt_in")
                  : props.emailOptIn),
          })
        : reactive({
              name: oldInput(props.old, "name"),
              line1: oldInput(props.old, "line1"),
              line2: oldInput(props.old, "line2"),
              city: oldInput(props.old, "city"),
              state: oldInput(props.old, "state")
                  ? oldInput(props.old, "state")
                  : "",
              postal_code: oldInput(props.old, "postal_code"),
              country_code: country.value.code,
              email: oldInput(props.old, "email"),
              phone: oldInput(props.old, "phone"),
              default: !!oldInput(props.old, "default"),
              verify: !!(Object.prototype.hasOwnProperty.call(
                  props.old,
                  "verify",
              )
                  ? oldInput(props.old, "verify")
                  : true),
              email_opt_in: !!(Object.prototype.hasOwnProperty.call(
                  props.old,
                  "email_opt_in",
              )
                  ? oldInput(props.old, "email_opt_in")
                  : props.emailOptIn),
          });

function onPlaceChanged() {
    refs.line1.value.value = inputs.line1;
    pasteAutocompletedAddress(inputs);
    onAddressChanged();
}

function updateCountry(countryCode?: any) {
    updateCountryRequest({
        country_code:
            countryCode && countryCode.length === 2
                ? countryCode
                : inputs.country_code,
    }).then((data: any) => {
        inputs.country_code = data.country.code;
        country.value = data.country;
        states.value = data.states;
        restrictAutocompleteByCountry(country.value.code);
    });
}

function onAddressChanged() {
    verifyAddress(inputs);
}

onMounted(() => {
    initializeAutocomplete(
        refs.line1.value,
        country.value.code,
        onPlaceChanged,
    );

    switch (true) {
        case !!hasError(props.errors, "name"):
            return refs.name.value.focus();
        case !!hasError(props.errors, "line1"):
            return refs.line1.value.focus();
        case !!hasError(props.errors, "line2"):
            return refs.line2.value.focus();
        case !!hasError(props.errors, "city"):
            return refs.city.value.focus();
        case !!hasError(props.errors, "state"):
            return refs.state.value.focus();
        case !!hasError(props.errors, "postal_code"):
            return refs.postal_code.value.focus();
        case !!hasError(props.errors, "country_code"):
            return refs.country_code.value.focus();
        case !!hasError(props.errors, "email"):
            return refs.email.value.focus();
        case !!hasError(props.errors, "phone"):
            return refs.phone.value.focus();
        default:
            return refs.name.value.focus();
    }
});

onBeforeUnmount(() => destroyAutocomplete());
</script>
